import {createReducer, on} from '@ngrx/store';
import {
  ClustersState,
  clustersAdapter,
  initialClustersState,
} from './clusters.state';
import * as ClustersActions from './clusters.actions';

/**
 * Reducer for clusters state management
 */
export const clustersReducer = createReducer(
  initialClustersState,

  // ==================== Load All Clusters ====================

  on(ClustersActions.loadClusters, (state: ClustersState): ClustersState => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(
    ClustersActions.loadClustersSuccess,
    (state: ClustersState, { clusters }): ClustersState =>
      clustersAdapter.setAll(clusters, {
        ...state,
        loading: false,
        loaded: true,
        lastUpdated: Date.now(),
        error: null,
      })
  ),

  on(
    ClustersActions.loadClustersFailure,
    (state, { error }): ClustersState => ({
      ...state,
      loading: false,
      error,
    })
  ),

  // ==================== Load Single Cluster by ID ====================

  on(
    ClustersActions.loadClusterById,
    (state, { id }): ClustersState => ({
      ...state,
      loadingById: {
        ...state.loadingById,
        [id]: true,
      },
    })
  ),

  on(
    ClustersActions.loadClusterByIdSuccess,
    (state, { cluster }): ClustersState =>
      clustersAdapter.upsertOne(cluster, {
        ...state,
        loadingById: {
          ...state.loadingById,
          [cluster.id]: false,
        },
      })
  ),

  on(
    ClustersActions.loadClusterByIdFailure,
    (state, { id, error }): ClustersState => ({
      ...state,
      loadingById: {
        ...state.loadingById,
        [id]: false,
      },
      error,
    })
  ),

  // ==================== Create Cluster ====================

  on(
    ClustersActions.createCluster,
    (state): ClustersState => ({
      ...state,
      creating: true,
      createError: null,
    })
  ),

  on(
    ClustersActions.createClusterSuccess,
    (state, { cluster }): ClustersState =>
      clustersAdapter.addOne(cluster, {
        ...state,
        creating: false,
        createError: null,
        selectedClusterId: cluster.id,
      })
  ),

  on(
    ClustersActions.createClusterFailure,
    (state, { error }): ClustersState => ({
      ...state,
      creating: false,
      createError: error,
    })
  ),

  // ==================== Delete Cluster ====================

  on(
    ClustersActions.deleteCluster,
    (state, { payload }): ClustersState => ({
      ...state,
      deleting: {
        ...state.deleting,
        [payload.clusterId]: true,
      },
      deleteError: {
        ...state.deleteError,
        [payload.clusterId]: null,
      },
    })
  ),

  on(
    ClustersActions.deleteClusterSuccess,
    (state: ClustersState, { id, cluster }): ClustersState =>
      clustersAdapter.updateOne(
        {
          id,
          changes: cluster,
        },
        {
          ...state,
          deleting: {
            ...state.deleting,
            [id]: false,
          },
          deleteError: {
            ...state.deleteError,
            [id]: null,
          },
        }
      )
  ),

  on(
    ClustersActions.deleteClusterFailure,
    (state, { id, error }): ClustersState => ({
      ...state,
      deleting: {
        ...state.deleting,
        [id]: false,
      },
      deleteError: {
        ...state.deleteError,
        [id]: error,
      },
    })
  ),

  // ==================== Start/Stop Cluster ====================

  on(
    ClustersActions.startCluster,
    ClustersActions.stopCluster,
    (state, { id }): ClustersState => ({
      ...state,
      updating: {
        ...state.updating,
        [id]: true,
      },
      updateError: {
        ...state.updateError,
        [id]: null,
      },
    })
  ),

  on(
    ClustersActions.startClusterSuccess,
    ClustersActions.stopClusterSuccess,
    (state, { cluster }): ClustersState =>
      clustersAdapter.updateOne(
        {
          id: cluster.id,
          changes: cluster,
        },
        {
          ...state,
          updating: {
            ...state.updating,
            [cluster.id]: false,
          },
          updateError: {
            ...state.updateError,
            [cluster.id]: null,
          },
        }
      )
  ),

  on(
    ClustersActions.startClusterFailure,
    ClustersActions.stopClusterFailure,
    (state, { id, error }): ClustersState => ({
      ...state,
      updating: {
        ...state.updating,
        [id]: false,
      },
      updateError: {
        ...state.updateError,
        [id]: error,
      },
    })
  ),

  // ==================== Cluster Regions ====================

  on(ClustersActions.loadClusterRegions, (state: ClustersState): ClustersState => ({
    ...state,
    regionsLoading: true,
    regionsError: null,
  })),

  on(
    ClustersActions.loadClusterRegionsSuccess,
    (state: ClustersState, { regions }): ClustersState => ({
      ...state,
      regions,
      regionsLoading: false,
      regionsLoaded: true,
      regionsError: null,
    })
  ),

  on(
    ClustersActions.loadClusterRegionsFailure,
    (state, { error }): ClustersState => ({
      ...state,
      regionsLoading: false,
      regionsError: error,
    })
  ),

  // ==================== UI State Management ====================

  on(
    ClustersActions.selectCluster,
    (state, { id }): ClustersState => ({
      ...state,
      selectedClusterId: id,
    })
  ),

  on(
    ClustersActions.clearClusterErrors,
    (state): ClustersState => ({
      ...state,
      error: null,
      createError: null,
      updateError: {},
      deleteError: {},
      regionsError: null
    })
  ),

  on(
    ClustersActions.upsertCluster,
    (state, { cluster }): ClustersState =>
      clustersAdapter.upsertOne(cluster, state)
  ),

  on(
    ClustersActions.removeCluster,
    (state: ClustersState, { id }): ClustersState => {
      const newState: ClustersState = clustersAdapter.removeOne(id, state);

      // Clean up the tracking objects
      const {[id]: removedDeleting, ...remainingDeleting} = newState.deleting;
      const {[id]: removedDeleteError, ...remainingDeleteError} = newState.deleteError;
      const {[id]: removedUpdating, ...remainingUpdating} = newState.updating;
      const {[id]: removedUpdateError, ...remainingUpdateError} = newState.updateError;
      const {[id]: removedLoadingById, ...remainingLoadingById} = newState.loadingById;

      return {
        ...newState,
        deleting: remainingDeleting,
        deleteError: remainingDeleteError,
        updating: remainingUpdating,
        updateError: remainingUpdateError,
        loadingById: remainingLoadingById,
        selectedClusterId: newState.selectedClusterId === id ? null : newState.selectedClusterId,
      };
    }
  ),

  on(
    ClustersActions.resetClustersState,
    (): ClustersState => initialClustersState
  )
);
